import React, { useState, useRef } from 'react';
import { extractDocxTemplate } from './utils/docx';

/**
 * Document-template library modal — browse, create, edit, delete, and pick
 * saved QA document templates (server: /api/qa/template-library).
 *
 * Each template is 'private' (only its creator sees it) or 'shared' (visible to
 * everyone; only the creator can edit/delete). A template is either a hand-authored
 * section list, or a .docx "golden example" (format:'docx') whose full text is
 * stored as an `exemplar` the AI matches. When used, the parent passes its
 * { name, sections, exemplar } to /api/qa/generate-document as `customTemplate`.
 *
 * Props:
 *   open               boolean — render the modal
 *   onClose            () => void
 *   apiUrl             API base URL
 *   authenticatedFetch (url, opts) => Promise<Response> from the parent
 *   templates          saved templates the user can see (with `canEdit`)
 *   reload             () => Promise — re-fetch the saved list in the parent
 *   docTypeDefault     preselected docType for the create form (optional)
 *   prefill            open straight into the editor with this draft (optional)
 *   onUse              (template) => void — pick a template for the current doc
 */

const DOC_TYPE_OPTIONS = [
  { value: 'generic', label: 'Generic (any deliverable)' },
  { value: 'SRS', label: 'SRS' },
  { value: 'VEP', label: 'VEP' },
  { value: 'VEP-TC', label: 'Test Cases' },
  { value: 'SDS', label: 'SDS' },
];

const emptyDraft = (docTypeDefault) => ({
  id: null,
  name: '',
  docType: docTypeDefault || 'generic',
  description: '',
  visibility: 'private',
  format: 'sections',
  exemplar: '',
  sections: [{ title: '', guidance: '' }],
});

const TemplateLibrary = ({
  open,
  onClose,
  apiUrl,
  authenticatedFetch,
  templates = [],
  reload,
  docTypeDefault = 'generic',
  prefill = null,
  onUse,
}) => {
  const [view, setView] = useState(prefill ? 'edit' : 'list');
  const [draft, setDraft] = useState(prefill ? { ...emptyDraft(docTypeDefault), ...prefill } : emptyDraft(docTypeDefault));
  // When true, saving a newly-created template immediately selects it and closes.
  const [useAfterCreate, setUseAfterCreate] = useState(Boolean(prefill));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const docxInputRef = useRef(null);

  // Re-seed the editor whenever the modal is (re)opened with a new prefill.
  React.useEffect(() => {
    if (!open) return;
    if (prefill) {
      setDraft({ ...emptyDraft(docTypeDefault), ...prefill });
      setView('edit');
      setUseAfterCreate(true);
    } else {
      setView('list');
      setUseAfterCreate(false);
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  if (!open) return null;

  const startCreate = () => {
    setDraft(emptyDraft(docTypeDefault));
    setUseAfterCreate(false);
    setError(null);
    setView('edit');
  };

  const startEdit = (t) => {
    setDraft({
      id: t.id,
      name: t.name || '',
      docType: t.docType || 'generic',
      description: t.description || '',
      visibility: t.visibility || 'private',
      format: t.format || 'sections',
      // The list omits the (large) exemplar; the server keeps it on save unless
      // the owner replaces the .docx here.
      exemplar: t.exemplar || '',
      sections: (t.sections || []).map((s) => ({ title: s.title || '', guidance: s.guidance || '' })),
    });
    setUseAfterCreate(false);
    setError(null);
    setView('edit');
  };

  // Parse an uploaded .docx into a template draft. `forEdit` keeps the current
  // draft's name/metadata and just swaps in the new example + parsed sections.
  const parseDocxInto = async (file, forEdit) => {
    setBusy(true);
    setError(null);
    try {
      const { exemplar, sections } = await extractDocxTemplate(file);
      if (!exemplar) throw new Error('could not read any text from the .docx');
      const secs = sections.length
        ? sections.map((s) => ({ title: s.title || '', guidance: s.guidance || '' }))
        : [{ title: 'Document', guidance: 'Follow the structure and formatting of the example document.' }];
      if (forEdit) {
        setDraft((d) => ({ ...d, format: 'docx', exemplar, sections: secs, name: d.name || file.name.replace(/\.docx$/i, '') }));
      } else {
        setDraft({
          ...emptyDraft(docTypeDefault),
          name: file.name.replace(/\.docx$/i, ''),
          format: 'docx',
          exemplar,
          sections: secs,
        });
        setUseAfterCreate(false);
        setView('edit');
      }
    } catch (err) {
      setError(`Could not read .docx: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  const onPickDocx = (event, forEdit) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    if (file) parseDocxInto(file, forEdit);
  };

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setSection = (i, key, value) => setDraft((d) => ({
    ...d,
    sections: d.sections.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
  }));
  const addSection = () => setDraft((d) => ({ ...d, sections: [...d.sections, { title: '', guidance: '' }] }));
  const removeSection = (i) => setDraft((d) => ({
    ...d,
    sections: d.sections.length > 1 ? d.sections.filter((_, idx) => idx !== i) : d.sections,
  }));
  const moveSection = (i, dir) => setDraft((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.sections.length) return d;
    const next = [...d.sections];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...d, sections: next };
  });

  const validate = () => {
    if (!draft.name.trim()) return 'Give the template a name.';
    if (draft.format === 'docx' && !draft.id && !draft.exemplar.trim()) {
      return 'Upload a .docx example for this template.';
    }
    const secs = draft.sections.map((s) => ({ title: s.title.trim(), guidance: s.guidance.trim() }));
    if (!secs.length || secs.some((s) => !s.title)) return 'Every section needs a title.';
    return null;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    const payload = {
      name: draft.name.trim(),
      docType: draft.docType,
      description: draft.description.trim(),
      visibility: draft.visibility,
      format: draft.format || 'sections',
      sections: draft.sections.map((s) => ({ title: s.title.trim(), guidance: s.guidance.trim() })),
    };
    // Only send the exemplar when we actually have one (a fresh upload). On edit
    // without re-upload it's blank locally and the server keeps the stored copy.
    if (draft.format === 'docx' && draft.exemplar.trim()) payload.exemplar = draft.exemplar;
    try {
      const isEdit = Boolean(draft.id);
      const res = await authenticatedFetch(
        `${apiUrl}/api/qa/template-library${isEdit ? `/${draft.id}` : ''}`,
        { method: isEdit ? 'PATCH' : 'POST', body: JSON.stringify(payload) },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Request failed (${res.status})`);
      if (reload) await reload();
      if (!isEdit && useAfterCreate && onUse) {
        onUse(data.template);
        onClose();
        return;
      }
      setView('list');
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/template-library/${t.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `Delete failed (${res.status})`);
      if (reload) await reload();
    } catch (err) {
      setError(err.message || 'Failed to delete template');
    } finally {
      setBusy(false);
    }
  };

  const docTypeLabel = (dt) => (DOC_TYPE_OPTIONS.find((o) => o.value === dt)?.label || dt);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {view === 'list' ? '📚 Template library' : (draft.id ? '✏️ Edit template' : '➕ New template')}
          </h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {view === 'list' ? (
          <div style={styles.body}>
            <div style={styles.listActions}>
              <button style={styles.primaryBtn} onClick={startCreate}>➕ New template</button>
              <button style={styles.secondaryBtn} onClick={() => docxInputRef.current?.click()} disabled={busy}>⬆️ Upload .docx example</button>
              <input
                ref={docxInputRef}
                type="file"
                accept=".docx"
                onChange={(e) => onPickDocx(e, false)}
                style={{ display: 'none' }}
              />
              <span style={styles.help}>Private templates are only visible to you; shared ones are available to everyone. Upload a .docx to reuse a finished document as the target style.</span>
            </div>
            {templates.length === 0 ? (
              <p style={styles.empty}>No saved templates yet. Create one, or use “Save current as template” on a document.</p>
            ) : (
              <div style={styles.list}>
                {templates.map((t) => (
                  <div key={t.id} style={styles.card}>
                    <div style={styles.cardMain}>
                      <div style={styles.cardTitleRow}>
                        <span style={styles.cardName}>{t.name}</span>
                        <span style={t.visibility === 'shared' ? styles.badgeShared : styles.badgePrivate}>
                          {t.visibility === 'shared' ? 'shared' : 'private'}
                        </span>
                        <span style={styles.badgeType}>{docTypeLabel(t.docType)}</span>
                        {t.format === 'docx' && <span style={styles.badgeDocx}>.docx example</span>}
                      </div>
                      {t.description && <p style={styles.cardDesc}>{t.description}</p>}
                      <p style={styles.cardMeta}>
                        {t.sections?.length || 0} section{(t.sections?.length || 0) === 1 ? '' : 's'}
                        {t.createdByName ? ` · by ${t.createdByName}` : ''}
                      </p>
                    </div>
                    <div style={styles.cardActions}>
                      {onUse && (
                        <button style={styles.useBtn} onClick={() => { onUse(t); onClose(); }}>Use</button>
                      )}
                      {t.canEdit && (
                        <>
                          <button style={styles.linkBtn} onClick={() => startEdit(t)}>Edit</button>
                          <button style={styles.dangerLink} onClick={() => handleDelete(t)} disabled={busy}>Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={styles.body}>
            <div style={styles.formRow}>
              <label style={styles.label}>Name</label>
              <input style={styles.input} value={draft.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Acme SRS (house style)" />
            </div>
            <div style={styles.twoCol}>
              <div style={styles.formRow}>
                <label style={styles.label}>Applies to</label>
                <select style={styles.input} value={draft.docType} onChange={(e) => setField('docType', e.target.value)}>
                  {DOC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Visibility</label>
                <div style={styles.radioRow}>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="visibility" checked={draft.visibility === 'private'} onChange={() => setField('visibility', 'private')} /> Private
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="visibility" checked={draft.visibility === 'shared'} onChange={() => setField('visibility', 'shared')} /> Shared
                  </label>
                </div>
              </div>
            </div>
            <div style={styles.formRow}>
              <label style={styles.label}>Description <span style={styles.optional}>(optional)</span></label>
              <input style={styles.input} value={draft.description} onChange={(e) => setField('description', e.target.value)} placeholder="Short summary of what this template is for" />
            </div>

            {draft.format === 'docx' && (
              <div style={styles.docxBanner}>
                <div style={styles.docxBannerHead}>
                  <span style={styles.badgeDocx}>.docx example</span>
                  <span style={styles.help}>
                    The AI will match this example’s structure, tone, and formatting. Sections below were detected from its headings.
                  </span>
                </div>
                {draft.exemplar ? (
                  <>
                    <textarea style={styles.exemplarPreview} value={draft.exemplar} readOnly rows={6} />
                    <span style={styles.help}>{draft.exemplar.length.toLocaleString()} characters captured from the example.</span>
                  </>
                ) : (
                  <span style={styles.help}>Example text is stored on the server. Upload a new .docx to replace it.</span>
                )}
                <div>
                  <button style={styles.secondaryBtn} onClick={() => docxInputRef.current?.click()} disabled={busy}>
                    {draft.exemplar ? 'Replace .docx example' : 'Upload .docx example'}
                  </button>
                  <input
                    ref={docxInputRef}
                    type="file"
                    accept=".docx"
                    onChange={(e) => onPickDocx(e, true)}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            )}

            <div style={styles.sectionsHead}>
              <label style={styles.label}>Sections</label>
              <span style={styles.help}>Each section becomes a heading the AI fills in using your guidance.</span>
            </div>
            {draft.sections.map((s, i) => (
              <div key={i} style={styles.sectionCard}>
                <div style={styles.sectionTop}>
                  <span style={styles.sectionNum}>{i + 1}</span>
                  <input
                    style={styles.sectionTitle}
                    value={s.title}
                    onChange={(e) => setSection(i, 'title', e.target.value)}
                    placeholder="Section title (e.g. 4. Functional Requirements)"
                  />
                  <button style={styles.iconBtn} onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up">↑</button>
                  <button style={styles.iconBtn} onClick={() => moveSection(i, 1)} disabled={i === draft.sections.length - 1} title="Move down">↓</button>
                  <button style={styles.iconBtnDanger} onClick={() => removeSection(i)} disabled={draft.sections.length === 1} title="Remove">✕</button>
                </div>
                <textarea
                  style={styles.sectionGuidance}
                  value={s.guidance}
                  onChange={(e) => setSection(i, 'guidance', e.target.value)}
                  placeholder="Guidance: what the AI should write in this section"
                  rows={3}
                />
              </div>
            ))}
            <button style={styles.addBtn} onClick={addSection}>＋ Add section</button>

            <div style={styles.footer}>
              <button style={styles.secondaryBtn} onClick={() => (prefill ? onClose() : setView('list'))} disabled={busy}>Cancel</button>
              <button style={styles.primaryBtn} onClick={handleSave} disabled={busy}>
                {busy ? 'Saving…' : (draft.id ? 'Save changes' : 'Create template')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
  },
  modal: {
    background: '#fff', borderRadius: 12, width: 'min(760px, 100%)', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #eee',
  },
  title: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  closeBtn: { border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#666' },
  body: { padding: 20, overflowY: 'auto' },
  error: { margin: '12px 20px 0', padding: '8px 12px', background: '#fdecea', color: '#b3261e', borderRadius: 8, fontSize: 13 },
  help: { color: '#666', fontSize: 12 },
  empty: { color: '#666', fontSize: 14, padding: '20px 0', textAlign: 'center' },
  listActions: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardName: { fontWeight: 600, color: '#1a1a1a' },
  cardDesc: { margin: '4px 0 0', color: '#444', fontSize: 13 },
  cardMeta: { margin: '4px 0 0', color: '#888', fontSize: 12 },
  cardActions: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  badgeShared: { background: '#e6f4ea', color: '#137333', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  badgePrivate: { background: '#eef1f5', color: '#5f6368', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  badgeType: { background: '#e8f0fe', color: '#1a56db', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  badgeDocx: { background: '#fef3e0', color: '#b26a00', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  docxBanner: { display: 'flex', flexDirection: 'column', gap: 8, padding: 12, border: '1px solid #f0d9b5', background: '#fffaf2', borderRadius: 8, marginBottom: 16 },
  docxBannerHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  exemplarPreview: { width: '100%', padding: '8px 10px', border: '1px solid #e0cba0', borderRadius: 8, fontSize: 12, fontFamily: 'SFMono-Regular, Consolas, monospace', background: '#fff', color: '#444', boxSizing: 'border-box', resize: 'vertical' },
  useBtn: { background: '#1a56db', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 },
  linkBtn: { border: 'none', background: 'transparent', color: '#1a56db', cursor: 'pointer', fontSize: 13 },
  dangerLink: { border: 'none', background: 'transparent', color: '#b3261e', cursor: 'pointer', fontSize: 13 },
  formRow: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, flex: 1 },
  twoCol: { display: 'flex', gap: 16 },
  label: { fontWeight: 600, fontSize: 13, color: '#333' },
  optional: { fontWeight: 400, color: '#999' },
  input: { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  radioRow: { display: 'flex', gap: 16, alignItems: 'center', paddingTop: 6 },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#333', cursor: 'pointer' },
  sectionsHead: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 },
  sectionCard: { border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, marginBottom: 10 },
  sectionTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  sectionNum: {
    width: 22, height: 22, borderRadius: 11, background: '#eef1f5', color: '#5f6368',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  sectionTitle: { flex: 1, padding: '7px 10px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14 },
  sectionGuidance: { width: '100%', padding: '7px 10px', border: '1px solid #ccc', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' },
  iconBtn: { border: '1px solid #ddd', background: '#fff', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: '#555' },
  iconBtnDanger: { border: '1px solid #f2c4c0', background: '#fff', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: '#b3261e' },
  addBtn: { border: '1px dashed #bbb', background: '#fafafa', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: '#444', fontSize: 13 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, borderTop: '1px solid #eee', paddingTop: 16 },
  primaryBtn: { background: '#1a56db', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 14 },
  secondaryBtn: { background: '#fff', color: '#444', border: '1px solid #ccc', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 14 },
};

export default TemplateLibrary;
